import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getRealTimeMetricsApi,
  getDailyReportsApi,
  getCampaignPerformanceApi,
} from '../api/analytics.api';
import { getCampaignAnalyticsApi, retryCampaignApi } from '../api/campaigns.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';

/* ─── Campaign analytics sub-components ─── */

function KpiCard({ icon, iconBg, iconColor, label, value, sub }) {
  return (
    <div
      style={{
        background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12,
        padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
        flex: '1 1 130px', minWidth: 120,
      }}
    >
      <div
        style={{
          width: 40, height: 40, borderRadius: 10, background: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <i className={`fas ${icon}`} style={{ fontSize: 14, color: iconColor }} />
      </div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', letterSpacing: -0.5 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#64748B', fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function BreakdownTable({ title, rows, keyLabel, valLabel }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginBottom: 10 }}>{title}</div>
      <table className="data-table" style={{ fontSize: 12 }}>
        <thead>
          <tr><th>{keyLabel}</th><th>{valLabel}</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ fontWeight: 600 }}>{r.key || r.state || r.plan || r.label || '—'}</td>
              <td>{r.count ?? r.value ?? r.users ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Campaign Detail Panel ─── */
function CampaignDetailReport({ campaignId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [retrying, setRetrying] = useState(false);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      const result = await apiHandler(() => getCampaignAnalyticsApi(campaignId));
      if (result.success) setData(unwrapData(result));
      else showToast(result.message || 'Failed to load analytics', 'error');
      setLoading(false);
    }
    load();
  }, [campaignId]);

  async function handleRetry() {
    setRetrying(true);
    const result = await apiHandler(() => retryCampaignApi(campaignId));
    setRetrying(false);
    showToast(
      result.success ? 'Retry queued for failed deliveries' : (result.message || 'No failed deliveries to retry'),
      result.success ? 'success' : 'info'
    );
  }

  const kpi = data?.kpi || {};
  const kpiCards = [
    { icon: 'fa-paper-plane',  iconBg: '#EFF6FF', iconColor: '#074f78', label: 'Total Sent',  value: kpi.totalSent?.toLocaleString()  ?? '—' },
    { icon: 'fa-circle-check', iconBg: '#F0FDF4', iconColor: '#15803D', label: 'Delivered',   value: kpi.delivered?.toLocaleString()  ?? '—', sub: kpi.deliveryRate  != null ? `${kpi.deliveryRate}% rate`  : null },
    { icon: 'fa-eye',          iconBg: '#FFF7ED', iconColor: '#ec7426', label: 'Opened',      value: kpi.opened?.toLocaleString()     ?? '—', sub: kpi.openRate      != null ? `${kpi.openRate}% rate`      : null },
    { icon: 'fa-reply',        iconBg: '#F0FDF4', iconColor: '#2E7D32', label: 'Responded',   value: kpi.responded?.toLocaleString()  ?? '—', sub: kpi.responseRate  != null ? `${kpi.responseRate}% rate`  : null },
    { icon: 'fa-circle-xmark', iconBg: '#FEF2F2', iconColor: '#B91C1C', label: 'Failed',      value: kpi.failed?.toLocaleString()     ?? '—' },
    { icon: 'fa-user-slash',   iconBg: '#F8FAFC', iconColor: '#64748B', label: 'Opt-outs',    value: kpi.optOut?.toLocaleString()     ?? '—' },
  ];

  return (
    <>
      {toast && (
        <div style={{
          position: 'fixed', top: 70, right: 20, zIndex: 9999,
          background: toast.type === 'success' ? '#F0FDF4' : toast.type === 'error' ? '#FEF2F2' : '#EFF6FF',
          border: `1px solid ${toast.type === 'success' ? '#b7e5bb' : toast.type === 'error' ? '#FECACA' : '#BFDBFE'}`,
          color: toast.type === 'success' ? '#15803D' : toast.type === 'error' ? '#B91C1C' : '#1D4ED8',
          padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,.08)',
        }}>
          <i className={`fas ${toast.type === 'success' ? 'fa-circle-check' : 'fa-circle-info'}`} style={{ marginRight: 7 }} />
          {toast.msg}
        </div>
      )}

      {/* Detail header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <button
          type="button"
          className="action-btn"
          style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
          onClick={onBack}
        >
          <i className="fas fa-arrow-left" style={{ fontSize: 11 }} /> Back to Reports
        </button>
        <button
          type="button"
          className="action-btn btn-orange"
          onClick={handleRetry}
          disabled={retrying}
        >
          <i className={`fas ${retrying ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} style={{ fontSize: 11 }} />
          {retrying ? 'Retrying…' : 'Retry Failed'}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 12, display: 'block' }} />
          Loading analytics…
        </div>
      ) : !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>No analytics data available.</div>
      ) : (
        <>
          {/* Campaign meta */}
          <div className="section-card mb-4 fade-up" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{data.campaignName}</div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 3, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: data.status === 'sent' ? '#F0FDF4' : '#F1F5F9',
                    color: data.status === 'sent' ? '#15803D' : '#64748B',
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  }}>
                    <i className={`fas ${data.status === 'sent' ? 'fa-check' : 'fa-circle'}`} style={{ fontSize: 8 }} />
                    {data.status}
                  </span>
                  {data.sentAt && <span>Sent {new Date(data.sentAt).toLocaleString()}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* KPI cards */}
          <div className="fade-up" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
            {kpiCards.map((card) => <KpiCard key={card.label} {...card} />)}
          </div>

          {/* Segment breakdown */}
          {(data.segmentBreakdown?.byState?.length > 0 || data.segmentBreakdown?.byPlan?.length > 0) && (
            <div className="section-card mb-4 fade-up" style={{ padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>
                <i className="fas fa-chart-pie" style={{ color: '#ec7426', marginRight: 8 }} /> Segment Breakdown
              </div>
              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <BreakdownTable title="By State" rows={data.segmentBreakdown.byState} keyLabel="State" valLabel="Count" />
                <BreakdownTable title="By Plan"  rows={data.segmentBreakdown.byPlan}  keyLabel="Plan"  valLabel="Count" />
              </div>
            </div>
          )}

          {/* Hourly stats */}
          {data.hourlyStats?.length > 0 && (
            <div className="section-card mb-4 fade-up" style={{ padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>
                <i className="fas fa-clock" style={{ color: '#074f78', marginRight: 8 }} /> Hourly Stats
              </div>
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead><tr><th>Hour</th><th>Sent</th><th>Delivered</th><th>Opened</th><th>Failed</th></tr></thead>
                <tbody>
                  {data.hourlyStats.map((h, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{h.hour ?? h.time ?? i}</td>
                      <td>{h.sent ?? '—'}</td><td>{h.delivered ?? '—'}</td><td>{h.opened ?? '—'}</td>
                      <td style={{ color: (h.failed ?? 0) > 0 ? '#B91C1C' : undefined }}>{h.failed ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Delivery log */}
          {data.deliveryLog?.length > 0 && (
            <div className="section-card mb-4 fade-up" style={{ padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>
                <i className="fas fa-list-check" style={{ color: '#2E7D32', marginRight: 8 }} /> Delivery Log
              </div>
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead><tr><th>Recipient</th><th>Channel</th><th>Status</th><th>Delivered At</th></tr></thead>
                <tbody>
                  {data.deliveryLog.slice(0, 50).map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{row.recipient || row.userId || row.email || '—'}</td>
                      <td>{row.channel || '—'}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 600, color: row.status === 'delivered' ? '#15803D' : row.status === 'failed' ? '#B91C1C' : '#64748B' }}>
                          {row.status || '—'}
                        </span>
                      </td>
                      <td style={{ color: '#64748B' }}>{row.deliveredAt ? new Date(row.deliveredAt).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Historical analytics */}
          {data.historicalAnalytics?.length > 0 && (
            <div className="section-card fade-up" style={{ padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 16 }}>
                <i className="fas fa-clock-rotate-left" style={{ color: '#64748B', marginRight: 8 }} /> Historical Analytics
              </div>
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead><tr><th>Date</th><th>Metric</th><th>Value</th><th>Opened</th><th>Delivered</th></tr></thead>
                <tbody>
                  {data.historicalAnalytics.map((h) => (
                    <tr key={h.id}>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{h.metricDate}</td>
                      <td style={{ fontWeight: 600 }}>{h.metricType}</td>
                      <td>{parseFloat(h.metricValue).toLocaleString()}</td>
                      <td>{h.segmentBreakdown?.opened ?? '—'}</td>
                      <td>{h.segmentBreakdown?.delivered ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty state */}
          {!data.deliveryLog?.length && !data.hourlyStats?.length &&
            !data.segmentBreakdown?.byState?.length && !data.historicalAnalytics?.length && (
            <div className="section-card fade-up" style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
              <i className="fas fa-chart-bar" style={{ fontSize: 32, marginBottom: 12, display: 'block', color: '#E2E8F0' }} />
              <div style={{ fontWeight: 600, marginBottom: 4 }}>No detailed data yet</div>
              <div style={{ fontSize: 13 }}>Analytics will populate as messages are delivered and opened.</div>
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ─── Main Reports Page ─── */
export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');

  const [realtime, setRealtime] = useState(null);
  const [daily, setDaily] = useState([]);
  const [campaignPerf, setCampaignPerf] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only load overview when not viewing a specific campaign
    if (campaignId) return;
    async function load() {
      setLoading(true);
      const [rt, dr, cp] = await Promise.all([
        apiHandler(() => getRealTimeMetricsApi()),
        apiHandler(() => getDailyReportsApi({ page: 1, limit: 10 })),
        apiHandler(() => getCampaignPerformanceApi()),
      ]);
      if (rt.success) setRealtime(unwrapData(rt));
      if (dr.success) setDaily(unwrapData(dr)?.reports || []);
      if (cp.success) setCampaignPerf(unwrapData(cp)?.performanceData || []);
      setLoading(false);
    }
    load();
  }, [campaignId]);

  // If a campaignId query param is present, render the campaign detail view
  if (campaignId) {
    return (
      <>
        <PageHeader title="Campaign Report" icon="fa-chart-line" trail={[{ label: 'Reports', path: '/reports' }, { label: 'Campaign Analytics' }]} />
        <CampaignDetailReport
          campaignId={campaignId}
          onBack={() => setSearchParams({})}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Reports" icon="fa-chart-bar" trail={[{ label: 'Reports' }]} />
      {loading ? (
        <p>Loading reports…</p>
      ) : (
        <>
          {realtime && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="stat-card">
                <div className="stat-num">{realtime.activeUsers ?? 0}</div>
                <div className="stat-label">Active users</div>
              </div>
              <div className="stat-card">
                <div className="stat-num">{realtime.newUsersToday ?? 0}</div>
                <div className="stat-label">New today</div>
              </div>
              <div className="stat-card">
                <div className="stat-num">{realtime.alertsToday ?? 0}</div>
                <div className="stat-label">Alerts today</div>
              </div>
              <div className="stat-card">
                <div className="stat-num">{realtime.campaignsToday ?? 0}</div>
                <div className="stat-label">Campaigns today</div>
              </div>
            </div>
          )}
          <div className="section-card mb-4">
            <h3 className="section-title mb-2">Daily Reports</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Users</th>
                  <th>Active</th>
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((r) => (
                  <tr key={r.id}>
                    <td>{r.reportDate}</td>
                    <td>{r.totalUsers}</td>
                    <td>{r.activeUsers}</td>
                    <td>{r.newUsers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="section-card">
            <h3 className="section-title mb-2">Campaign Performance</h3>
            <p style={{ fontSize: 13, color: '#64748B' }}>
              {campaignPerf.length} metric rows loaded
            </p>
          </div>
        </>
      )}
    </>
  );
}